import { useState } from "react";
import { Loader2 } from "lucide-react";
import { approveLauraStage } from "@/app/lib/auditflowApi";
import type { LauraStageState } from "@/app/lib/auditflowApi";
import type {
    LauraAudit,
    LauraP0State,
    LauraStageId,
} from "@/app/components/shared/types";
import { getNextStageId } from "./stageUtils";
import { useP0ApprovalGates } from "./p0/LauraP0Workspace";
import { useStageApprovalGates } from "./shared/LauraStageShell";
import { P0StepBadge } from "./p0/P0StepBadge";
import { useTranslation } from "@/app/i18n/LocaleContext";

interface Props {
    audit: LauraAudit;
    viewStage: LauraStageId;
    deliverableContent: string;
    p0State?: LauraP0State | null;
    planningStageState?: LauraStageState | null;
    onApproved: (audit: LauraAudit) => void;
}

export function LauraApproveBar({
    audit,
    viewStage,
    deliverableContent,
    p0State,
    planningStageState,
    onApproved,
}: Props) {
    const { t } = useTranslation();
    const isCurrent = audit.current_stage === viewStage;
    const next = getNextStageId(viewStage);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const p0Completion = p0State?.completion;
    const p0ApprovedDisplay = p0State?.approved_by_display;

    const { canApproveP0, allValidation: p0AllValidation, hasBriefing } =
        useP0ApprovalGates(
            viewStage === "p0" ? p0State ?? null : null,
            deliverableContent,
        );

    const planningGates = useStageApprovalGates(
        planningStageState ?? null,
        deliverableContent,
    );

    const isP0 = viewStage === "p0";
    const isPlanningStructured =
        viewStage === "p1" ||
        viewStage === "p2" ||
        viewStage === "p3" ||
        viewStage === "p4" ||
        viewStage === "p5" ||
        viewStage === "p6" ||
        viewStage === "p7";

    const canApprove = isP0
        ? canApproveP0
        : isPlanningStructured
          ? planningGates.canApprove
          : deliverableContent.trim().length > 0;

    if (!isCurrent && !p0Completion) return null;

    if (!isCurrent && p0Completion && viewStage === "p0") {
        const date = new Date(p0Completion.approved_at).toLocaleString();
        const user = p0ApprovedDisplay ?? p0Completion.approved_by;
        return (
            <div className="border-t bg-slate-50 px-4 py-3 text-sm text-gray-600">
                {t("laura", "p0ApprovedAt", { date, user })}
            </div>
        );
    }

    async function handleApprove() {
        setLoading(true);
        setError(null);
        try {
            const result = await approveLauraStage(audit.id, viewStage);
            onApproved({
                ...result.audit,
                stage_completions: result.stage_completions,
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : "Approval failed");
        } finally {
            setLoading(false);
        }
    }

    let statusMessage: string;
    if (isP0) {
        if (!hasBriefing) {
            statusMessage = "Generate and save the briefing before approving.";
        } else if (!p0AllValidation) {
            statusMessage =
                "Check all validation points before approving P0.";
        } else {
            statusMessage = `Ready to approve P0${next ? ` and advance to ${next.toUpperCase()}` : ""}?`;
        }
    } else if (isPlanningStructured) {
        if (!planningGates.hasDeliverable) {
            statusMessage = "Generate and save the deliverable before approving.";
        } else if (!planningGates.allValidation) {
            statusMessage = `Check all validation points before approving ${viewStage.toUpperCase()}.`;
        } else {
            statusMessage = `Ready to approve ${viewStage.toUpperCase()}${next ? ` and advance to ${next.toUpperCase()}` : ""}?`;
        }
    } else {
        statusMessage = canApprove
            ? `Ready to approve ${viewStage.toUpperCase()}${next ? ` and advance to ${next.toUpperCase()}` : ""}?`
            : "Add deliverable content before approving.";
    }

    return (
        <div className="flex flex-col gap-2 border-t bg-white px-4 py-3">
            {p0Completion && isP0 && (
                <p className="text-sm text-gray-600">
                    {t("laura", "p0ApprovedAt", {
                        date: new Date(p0Completion.approved_at).toLocaleString(),
                        user: p0ApprovedDisplay ?? p0Completion.approved_by,
                    })}
                </p>
            )}
            <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-gray-600">{statusMessage}</div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                {isCurrent && (
                    <button
                        type="button"
                        onClick={handleApprove}
                        disabled={loading || !canApprove}
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${
                            canApprove
                                ? "bg-emerald-600 hover:bg-emerald-700"
                                : "bg-emerald-700 opacity-50"
                        }`}
                    >
                        {isP0 && (
                            <P0StepBadge
                                step={7}
                                status={canApprove ? "done" : "pending"}
                                size="sm"
                            />
                        )}
                        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                        {isP0
                            ? t("laura", "p0ApproveAndAdvance")
                            : "Approve & advance"}
                    </button>
                )}
            </div>
        </div>
    );
}
